import { useCallback, useLayoutEffect, useRef } from "react";
import { useLocation, useSearch } from "wouter";

/** A location as the router sees it: the path, and the query with no `?`. */
export type ShellUrlLocation = { pathname: string; search: string };

/**
 * What a writer wants changed. Everything it does not name rides along
 * untouched — that is the whole point. A writer says which params are its own;
 * it never rebuilds the query from a copy it took at render time.
 */
export type ShellUrlPatch = {
  /** A new path. Omitted, the current one stays. */
  path?: string;
  /**
   * Params to set, or remove with `null`. An array writes one repeated param
   * per item (`?k=a&k=b`), the shape the sidebar's schema round-trips arrays in.
   */
  params?: Readonly<Record<string, string | readonly string[] | null>>;
  /** Remove every param whose key starts with this, before `params` applies. */
  dropPrefix?: string;
};

/**
 * A patch, or a function of the location the patch will apply to — for a writer
 * whose next value depends on what is there now, such as the sidebar capturing
 * the sidebar it is about to replace as its way back.
 */
export type ShellUrlPatchInput = ShellUrlPatch | ((current: ShellUrlLocation) => ShellUrlPatch);

export type ShellUrlWriteOptions = {
  /** Replace the history entry rather than pushing one. */
  replace?: boolean;
  /** History state to carry, e.g. the sidebar's full-navigation marker. */
  state?: unknown;
};

/** Apply a patch to a location. Pure, so it can be reasoned about in a test. */
export const applyShellUrlPatch = (
  current: ShellUrlLocation,
  patch: ShellUrlPatch,
): ShellUrlLocation => {
  const params = new URLSearchParams(current.search);
  if (patch.dropPrefix) {
    // Collected first: deleting while iterating skips every other key.
    for (const key of [...params.keys()]) {
      if (key.startsWith(patch.dropPrefix)) params.delete(key);
    }
  }
  for (const [key, value] of Object.entries(patch.params ?? {})) {
    params.delete(key);
    if (value === null) continue;
    if (typeof value === "string") params.set(key, value);
    else for (const item of value) params.append(key, item);
  }
  return { pathname: patch.path ?? current.pathname, search: params.toString() };
};

export const shellUrlHref = ({ pathname, search }: ShellUrlLocation): string =>
  search ? `${pathname}?${search}` : pathname;

/**
 * The location most recently written through the owner.
 *
 * Module-level because the URL is one thing: a write from the sidebar and a
 * write from the calendar in the same tick have to build on each other, and
 * they run in different components.
 *
 * Retired only when a writer renders at some *other* location — the Back
 * button, a Link, a write the router refused. A render at exactly this
 * location proves the write landed and changes nothing by keeping it; and
 * keeping it is what a component unmounting *because* of that write needs. Its
 * own snapshot is the location it last rendered at, before the navigation, and
 * it will never render again to learn the new one. Its cleanup runs in the
 * passive phase, after every surviving writer's layout effect — so a rule that
 * retired the write on any render would hand that cleanup the stale snapshot,
 * and a thread closing its sidebar on the way out would write the old thread's
 * path back over the destination the user had just chosen.
 */
let pending: ShellUrlLocation | null = null;

/**
 * The one writer of the app's URL.
 *
 * Every surface that patches the current address — the canvas destination and
 * its `from`, the right sidebar's `sb_*` keys, a consumed deep-link param, the
 * lightbox token — goes through this rather than reading the query at render
 * time and writing its own copy back. That copy was the bug: two writers in one
 * tick each rebuilt the query from a snapshot taken before the other wrote, and
 * whichever landed second erased the first. The sidebar's cleanup guard in the
 * thread view and the calendar's live read of `window.location` were both
 * working around it, one case at a time.
 *
 * Reads the router's location, not `window.location`, so it works over the
 * in-memory router the tests use; and reads the last write when one is still
 * in flight, so a second write in the same tick starts from the first.
 *
 * The returned function is stable, so a handler built on it stays stable too.
 */
export const useShellUrlWriter = () => {
  const [pathname, setLocation] = useLocation();
  const search = useSearch();

  // The latest rendered location, in a ref so the callback below can read it
  // at call time without re-creating on every navigation. Committed from the
  // effect rather than assigned during render, so the same effect can judge
  // the write in flight against what the router now shows: the same location
  // means it landed, and stays the base for anything still to write against it;
  // a different one means something else moved the URL, and the router's
  // snapshot is the truth again.
  const snapshot = useRef<ShellUrlLocation>({ pathname, search });
  useLayoutEffect(() => {
    const rendered = { pathname, search };
    snapshot.current = rendered;
    if (pending && shellUrlHref(pending) !== shellUrlHref(rendered)) pending = null;
  }, [pathname, search]);

  return useCallback(
    (input: ShellUrlPatchInput, options?: ShellUrlWriteOptions) => {
      const base = pending ?? snapshot.current;
      const patch = typeof input === "function" ? input(base) : input;
      const next = applyShellUrlPatch(base, patch);
      pending = next;
      setLocation(shellUrlHref(next), options);
    },
    [setLocation],
  );
};
